import React, { useCallback, type ComponentProps } from 'react';
import { Tabs } from 'expo-router';
import { defineMessages, FormattedMessage, type MessageDescriptor } from 'react-intl';

import { Navbar } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarLink = (props: Props) => {
  const { activeIndex, index, route, onAccountPress, onHomePress, onMenuPress } = props;

  const { name } = route;

  // ─────────────────────────────────────────────────────────────────────

  const config = checkIfNavbarTabName(name) ? NAVBAR_TAB_CONFIGS[name] : null;
  const isActive = activeIndex === index;

  // ─── Handlers ────────────────────────────────────────────────────────

  const onPress = useCallback(() => {
    if (name === HOME_TAB_NAME) {
      onHomePress();
    }

    if (name === MENU_TAB_NAME) {
      onMenuPress();
    }

    if (name === ACCOUNT_TAB_NAME) {
      onAccountPress();
    }
  }, [name, onHomePress, onMenuPress, onAccountPress]);

  // ─────────────────────────────────────────────────────────────────────

  if (!config) {
    return null;
  }

  return (
    <Navbar.Link
      iconName={config.iconName}
      iconNameActive={config.iconNameActive}
      isActive={isActive}
      onPress={onPress}
    >
      <FormattedMessage {...config.message} />
    </Navbar.Link>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfNavbarTabName(routeName: string): routeName is NavbarTabName {
  return NAVBAR_TAB_NAMES.some((navbarTabName) => navbarTabName === routeName);
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  account: {
    defaultMessage: 'Account',
    description: 'Navbar V2 | Account link label',
  },
  home: {
    defaultMessage: 'Home',
    description: 'Navbar V2 | Home link label',
  },
  menu: {
    defaultMessage: 'Menu',
    description: 'Navbar V2 | Menu link label',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const HOME_TAB_NAME = '(home)';
const MENU_TAB_NAME = '(menu)';
const ACCOUNT_TAB_NAME = 'account';

const NAVBAR_TAB_NAMES = [HOME_TAB_NAME, MENU_TAB_NAME, ACCOUNT_TAB_NAME] as const;

const NAVBAR_TAB_CONFIGS = {
  [HOME_TAB_NAME]: {
    iconName: 'IconHomeAlternative',
    iconNameActive: 'IconHomeAlternativeFill',
    message: messages.home,
  },
  [MENU_TAB_NAME]: {
    iconName: 'IconMenuBowl',
    iconNameActive: 'IconBowlFill',
    message: messages.menu,
  },
  [ACCOUNT_TAB_NAME]: {
    iconName: 'IconAccount',
    iconNameActive: 'IconAccountFill',
    message: messages.account,
  },
} satisfies Record<NavbarTabName, NavbarTabConfig>;

// ─── Types ──────────────────────────────────────────────────────────────────

type Props = {
  route: TabBarProps['state']['routes'][number];
  index: number;
  activeIndex: number;
  onAccountPress: () => void;
  onHomePress: () => void;
  onMenuPress: () => void;
};

type TabBarProps = Parameters<NonNullable<ComponentProps<typeof Tabs>['tabBar']>>[0];
type NavbarTabName = (typeof NAVBAR_TAB_NAMES)[number];

type NavbarTabConfig = {
  iconName: ComponentProps<typeof Navbar.Link>['iconName'];
  iconNameActive: ComponentProps<typeof Navbar.Link>['iconNameActive'];
  message: MessageDescriptor;
};
